<header class="phs__header">
  <h3 class="phs__title">
    {{ header().title }}@if (header().titleAlt) {<span class="phs__title-alt"> / {{ header().titleAlt }}</span>}
  </h3>

  <div class="phs__side">
    <span class="phs__side-text">{{ header().sideLabel }}</span>
    <span class="phs__glyph" aria-hidden="true">{{ header().planetA.glyph }}</span>
    <span class="phs__side-text">{{ header().planetA.name }}</span>
    <span class="phs__side-sep">&amp;</span>
    <span class="phs__glyph" aria-hidden="true">{{ header().planetB.glyph }}</span>
    <span class="phs__side-text">{{ header().planetB.name }}</span>
  </div>

  @if (header().tagline) {
    <p class="phs__tagline">{{ header().tagline }}</p>
  }
</header>

<!-- ════════ Uitgelicht: wat de pijl zegt, de waarden, en wat ze zo maakt ═ -->
<section class="phs__hero" aria-label="Samenvatting" data-test-id="phs-hero">
  <div class="phs__hero-arrow" data-test-id="phs-hero-arrow">
    <hd-transformation-arrow
      class="phs__hero-arrow-icon"
      [tone]="toneSection().number"
      [side]="side()"
      [category]="category()"
      [colorConfig]="colorConfig()"
      [interactive]="false"
    />
    <div class="phs__hero-arrow-text">
      <span class="phs__card-label">{{ heroArrow().label }}</span>
      <strong class="phs__hero-arrow-name" data-test-id="phs-arrow-keynote-name">{{ heroArrow().name }}</strong>
      @if (heroArrow().short) {
        <span class="phs__hero-arrow-short" data-test-id="phs-arrow-keynote-short">{{ heroArrow().short }}</span>
      }
      <span class="phs__card-source">{{ heroArrow().source }}</span>
    </div>
  </div>

  <div class="phs__cards">
    @for (card of heroCards(); track card.key) {
      <div class="phs__card" [attr.data-card]="card.key" [attr.data-test-id]="'phs-card-' + card.key">
        <span class="phs__card-label">{{ card.label }}</span>
        <span class="phs__card-value">
          @if (card.icon) {
            <i class="phs__card-icon fa-solid fa-{{ card.icon }}" aria-hidden="true"></i>
          }
          <span>{{ card.value }}</span>
        </span>
        @if (card.short) {
          <span class="phs__card-short">{{ card.short }}</span>
        }
        <span class="phs__card-source">{{ card.source }}</span>
      </div>
    }
  </div>

  <p class="phs__origin" data-test-id="phs-origin">
    <span class="phs__origin-label">{{ origin().label }}</span>
    <span class="phs__origin-item">
      <span class="phs__glyph" aria-hidden="true">{{ origin().glyph }}</span>
      <span>{{ origin().carrier }}</span>
      @if (origin().gateLine) {
        <b class="phs__origin-strong" data-test-id="phs-origin-gate-line">{{ origin().gateLine }}</b>
      }
    </span>
    <span class="phs__origin-item">
      <hd-color-shape class="phs__origin-shape" [colorNumber]="color()" [side]="side()" [colorConfig]="colorConfig()" />
      <span>{{ origin().colorText }}</span>
    </span>
    <span class="phs__origin-item">
      <hd-tone-shape class="phs__origin-shape phs__origin-shape--tone" [toneNumber]="tone()" [side]="side()" [colorConfig]="colorConfig()" />
      <span>{{ origin().toneText }}</span>
    </span>
    @if (origin().baseText) {
      <span class="phs__origin-item" data-test-id="phs-origin-base">{{ origin().baseText }}</span>
    }
  </p>
</section>

@if (header().intro) {
  <p class="phs__intro" data-test-id="phs-intro">{{ header().intro }}</p>
}

<!-- ════════ Overzicht: alles nog eens op één plek, net boven de kleur ═ -->
<dl class="phs__summary" aria-label="Samenvatting">
  @for (row of summaryRows(); track row.key) {
    <div class="phs__summary-row" [attr.data-row]="row.key">
      <dt class="phs__summary-label">{{ row.label }}</dt>
      <dd class="phs__summary-value">
        <span class="phs__summary-main-line">
          <span class="phs__summary-main">{{ row.value }}</span>
          @if (row.rightSub) {
            <span class="phs__summary-right-sub">{{ row.rightSub }}</span>
          }
        </span>
        @if (row.hint) {
          <span class="phs__summary-hint">{{ row.hint }}</span>
        }
      </dd>
    </div>
  }
</dl>

<!-- ════════ Kleur-sectie ════════════════════════════════════════════════ -->
<section class="phs__section phs__section--color" aria-label="Kleur">
  <div class="phs__section-head">
    <hd-color-shape
      class="phs__shape phs__shape--color"
      [colorNumber]="color()"
      [side]="side()"
      [colorConfig]="colorConfig()"
    />
    <h4 class="phs__section-title">
      {{ sharedLabels().colorLabel }} <b class="phs__num">{{ colorSection().number }}</b>
      <span class="phs__colon">:</span>
      <span class="phs__name">{{ colorSection().name }}</span>
      @if (colorSection().gloss) {
        <em class="phs__gloss">{{ colorSection().gloss }}</em>
      }
    </h4>
    @if (colorSection().icon) {
      <i class="phs__section-icon fa-solid fa-{{ colorSection().icon }}" aria-hidden="true"></i>
    }
  </div>

  @if (colorSection().groupName) {
    <p class="phs__meta phs__meta--group">
      <span class="phs__meta-label">Groep {{ colorSection().groupRange }}</span>
      <span class="phs__meta-sep" aria-hidden="true">·</span>
      <span class="phs__meta-value">{{ colorSection().groupName }}</span>
      @if (colorSection().groupKeynote) {
        <em class="phs__meta-em">— {{ colorSection().groupKeynote }}</em>
      }
    </p>
  }

  @if (colorSection().short) {
    <p class="phs__short">{{ colorSection().short }}</p>
  }
  @if (colorSection().body) {
    <p class="phs__body">{{ colorSection().body }}</p>
  }
</section>

<!-- ════════ Partner-sectie (enkel personality — tussen kleur en toon) ═══ -->
@if (partnerSection(); as partner) {
  <section class="phs__section phs__section--partner" aria-label="Partner">
    <div class="phs__section-head">
      <span class="phs__section-marker phs__section-marker--partner" aria-hidden="true">↔</span>
      <h4 class="phs__section-title phs__section-title--partner">
        {{ partner.label }}
      </h4>
    </div>
    <p class="phs__partner-row">
      <span class="phs__partner-side">
        <b class="phs__num">{{ partner.thisNumber }}</b>
        <span class="phs__name">{{ partner.thisName }}</span>
      </span>
      <span class="phs__partner-arrow" aria-hidden="true">↔</span>
      <span class="phs__partner-side">
        <b class="phs__num">{{ partner.partnerNumber }}</b>
        <span class="phs__name">{{ partner.partnerName }}</span>
      </span>
    </p>
  </section>
}

<!-- ════════ Toon-sectie ═════════════════════════════════════════════════ -->
<section class="phs__section phs__section--tone" aria-label="Toon">
  <div class="phs__section-head">
    <hd-tone-scale
      class="phs__tone-scale"
      [tone]="toneSection().number"
      [color]="toneScaleAccent()"
      [showArrow]="true"
    />
    <hd-tone-shape
      class="phs__shape phs__shape--tone"
      [toneNumber]="toneSection().number"
      [side]="side()"
      [colorConfig]="colorConfig()"
    />
    <h4 class="phs__section-title">
      {{ sharedLabels().toneLabel }} <b class="phs__num">{{ toneSection().number }}</b>
      <span class="phs__colon">:</span>
      <span class="phs__name">{{ toneSection().variantName }}</span>
      @if (toneSection().variantGloss) {
        <em class="phs__gloss">{{ toneSection().variantGloss }}</em>
      }
    </h4>
    @if (toneSection().variantIcon) {
      <i class="phs__section-icon fa-solid fa-{{ toneSection().variantIcon }}" aria-hidden="true"></i>
    }
  </div>

  @if (toneSection().variantShort) {
    <p class="phs__short">{{ toneSection().variantShort }}</p>
  }
  @if (toneSection().variantBody) {
    <p class="phs__body" data-test-id="phs-tone-variant-body">{{ toneSection().variantBody }}</p>
  }

  <!-- De toon bepaalt ook de cognitie (resonantie/gevoel) en de pijlrichting:
       hun uitleg hoort bij de toon, niet enkel als keynote in de kaartjes bovenaan. -->
  @if (toneSection().cognitionBody) {
    <div class="phs__detail" data-test-id="phs-tone-cognition-detail">
      <h5 class="phs__detail-title">
        @if (toneSection().cognitionIcon) {
          <i class="phs__row-icon fa-solid fa-{{ toneSection().cognitionIcon }}" aria-hidden="true"></i>
        }
        <span class="phs__row-label">{{ categoryLabels().cognitionLabel }}</span>
        <span class="phs__row-sep" aria-hidden="true">·</span>
        <span class="phs__row-value">{{ toneSection().cognitionName }}</span>
      </h5>
      <p class="phs__body">{{ toneSection().cognitionBody }}</p>
    </div>
  }
  @if (toneSection().brainPoleBody) {
    <div class="phs__detail" data-test-id="phs-tone-brain-detail">
      <h5 class="phs__detail-title">
        @if (toneSection().brainPoleIcon) {
          <i class="phs__row-icon fa-solid fa-{{ toneSection().brainPoleIcon }}" aria-hidden="true"></i>
        }
        <span class="phs__row-label">{{ categoryLabels().brainLabel }}</span>
        <span class="phs__row-sep" aria-hidden="true">·</span>
        <span class="phs__row-value">{{ toneSection().brainPoleName }}</span>
      </h5>
      <p class="phs__body">{{ toneSection().brainPoleBody }}</p>
    </div>
  }
</section>

<!-- ════════ Traject-sectie (enkel personality — onder de toon-sectie) ═══ -->
@if (trajectorySection(); as trajectory) {
  <section class="phs__section phs__section--trajectory" aria-label="Traject">
    <div class="phs__section-head">
      <span class="phs__section-marker phs__section-marker--trajectory" aria-hidden="true">→</span>
      <h4 class="phs__section-title phs__section-title--trajectory">
        {{ trajectory.label }}
      </h4>
    </div>
    <p class="phs__trajectory-row">
      <span class="phs__trajectory-from">{{ trajectory.fromName }}</span>
      <span class="phs__trajectory-arrow" aria-hidden="true">→</span>
      <span class="phs__trajectory-to">{{ trajectory.toName }}</span>
    </p>
    @if (trajectory.body) {
      <p class="phs__body" data-test-id="phs-trajectory-body">{{ trajectory.body }}</p>
    }
    @if (trajectory.intro) {
      <p class="phs__trajectory-intro" data-test-id="phs-trajectory-intro">{{ trajectory.intro }}</p>
    }
  </section>
}

<!-- ════════ Het niet-fysieke (enkel Motivation — onder het traject) ═══ -->
@if (nonPhysicalSection(); as nonPhysical) {
  <section class="phs__section phs__section--non-physical" [attr.aria-label]="nonPhysical.label" data-test-id="phs-non-physical">
    <div class="phs__section-head">
      <i class="phs__section-marker phs__section-marker--non-physical fa-solid fa-sparkles" aria-hidden="true"></i>
      <h4 class="phs__section-title">{{ nonPhysical.label }}</h4>
    </div>
    <p class="phs__body" data-test-id="phs-non-physical-body">{{ nonPhysical.body }}</p>
    @if (nonPhysical.intro) {
      <p class="phs__trajectory-intro" data-test-id="phs-non-physical-intro">{{ nonPhysical.intro }}</p>
    }
  </section>
}

@if (header().helper) {
  <p class="phs__helper">{{ header().helper }}</p>
}
